import { CloseOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import React, { useEffect, useRef } from "react";
import ReactPlayer from "react-player";

import "./VideoPlayer.css";

const MEDIA_HOSTS = "hls-video, dash-video, youtube-video, vimeo-video, mux-video, twitch-video, wistia-video";

/**
 * React creates host instances (and sets their attributes) during the render phase; while
 * react-player lazy-loads its backend the tree may be replayed, so the media custom element
 * fires `autoplay` playback from attributeChangedCallback before (or without ever) being
 * inserted into the DOM. A discarded twin then plays audio forever, unreachable from the DOM.
 * Rule: detached media never plays. A play() on a detached element is deferred until the
 * element connects (the legit pre-commit race) and silently dropped if it never does (ghost).
 */
let playGuardInstalled = false;
const installDetachedPlayGuard = (): void => {
  if (playGuardInstalled || typeof window === "undefined") return;
  playGuardInstalled = true;
  const originalPlay = HTMLMediaElement.prototype.play;
  HTMLMediaElement.prototype.play = function (this: HTMLMediaElement): Promise<void> {
    if (this.isConnected) return originalPlay.call(this);
    return new Promise((resolve) => {
      let attempts = 0;
      const retry = () => {
        if (this.isConnected) {
          originalPlay.call(this).then(resolve, () => resolve());
        } else if (++attempts >= 100) {
          resolve();
        } else {
          setTimeout(retry, 100);
        }
      };
      retry();
    });
  };
};

/**
 * The media-chrome custom elements react-player v3 renders (hls-video, youtube-video, ...)
 * don't tear down on disconnect — a detached element keeps streaming audio (hls.js keeps
 * feeding the native <video> hidden in the shadow root). Passing a cleanup ref through
 * ReactPlayer doesn't work either: it invokes the forwarded ref manually and discards the
 * returned cleanup. So we stop everything ourselves from a wrapping container.
 */
const stopPlayback = (container: HTMLElement): void => {
  for (const media of container.querySelectorAll<HTMLMediaElement>("video, audio")) {
    media.pause();
    media.removeAttribute("src");
    media.load();
  }
  for (const host of container.querySelectorAll<HTMLElement>(MEDIA_HOSTS)) {
    for (const media of host.shadowRoot?.querySelectorAll<HTMLMediaElement>("video, audio") ?? []) {
      media.pause();
      media.removeAttribute("src");
      media.load();
    }
    // Triggers the element's internal load() -> destroy path (e.g. hls.js teardown).
    host.removeAttribute("src");
  }
};

const StoppablePlayer = ({ src }: { src: string }): React.ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null);

  installDetachedPlayGuard();

  useEffect(() => {
    const container = containerRef.current;
    return () => {
      if (container) stopPlayback(container);
    };
  }, []);

  return (
    <div ref={containerRef} style={{ display: "contents" }}>
      <ReactPlayer src={src} controls autoPlay config={{ vimeo: { controls: true } }} />
    </div>
  );
};

interface VideoPlayerProps {
  videoUrl: string | null;
  onClose?: () => void;
  visible: boolean;
}

const VideoPlayer = ({ videoUrl, onClose, visible }: VideoPlayerProps): React.ReactElement => {
  useEffect(() => {
    if (!visible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [visible, onClose]);

  const closeButton = (
    <button
      onClick={onClose}
      className="absolute -right-10 -top-1 z-50 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/40"
    >
      <CloseOutlined />
    </button>
  );

  if (videoUrl?.endsWith(".gif")) {
    return (
      <Modal
        title={null}
        open={visible}
        destroyOnHidden
        footer={null}
        onCancel={onClose}
        centered
        closable={false}
        wrapClassName="video-player-modal"
        width="auto"
        styles={{
          body: {
            padding: 0,
            display: "flex",
            justifyContent: "center",
            background: "#000",
            borderRadius: 12,
            overflow: "hidden",
          },
          mask: { background: "rgba(0,0,0,0.75)" },
        }}
      >
        {closeButton}
        <img src={videoUrl} className="max-h-[80vh] w-auto" />
      </Modal>
    );
  }

  return (
    <Modal
      title={null}
      open={visible}
      destroyOnHidden
      footer={null}
      onCancel={onClose}
      centered
      closable={false}
      wrapClassName="video-player-modal"
      width="auto"
      styles={{
        body: { padding: 0, background: "#000", borderRadius: 12, overflow: "hidden", lineHeight: 0 },
        mask: { background: "rgba(0,0,0,0.75)" },
      }}
    >
      {closeButton}
      {visible && videoUrl ? <StoppablePlayer src={videoUrl} /> : null}
    </Modal>
  );
};

export default VideoPlayer;
